// TicketPreview.jsx — on-screen render of the thermal golden tickets.
// Faithful HTML mirror of buildPrintBuffer() in utils/printer.js (the ePOS
// command stream), so /register ticket layouts can be exercised and iterated
// with no Epson on the network. QR is rendered locally via the `qrcode`
// package at error-correction Q — same level as the printed symbol, and
// token URLs never leave the device.
//
// KEEP IN SYNC: any layout change in buildPrintBuffer should land here too.
// Props mirror the print job object: { ticketType, orderUrl, expiryDisplay,
// serial, tagline, subtitle, hasPancake, showSignature }.

import { useEffect, useState } from "react";
import QRCode from "qrcode";

const PAPER = "#fff7d6"; // yellow thermal stock
const INK = "#241c10";

const mono = {
  fontFamily: "'Courier New', Courier, monospace",
  color: INK,
  lineHeight: 1.35,
  whiteSpace: "pre",
};

function Line({ children, size = 1, bold = false, reverse = false, h2 = false }) {
  return (
    <div style={{
      ...mono,
      fontSize: size === 2 ? "1.5rem" : h2 ? "1.05rem" : "0.78rem",
      fontWeight: bold ? 800 : 500,
      ...(reverse ? { background: INK, color: PAPER, display: "inline-block", padding: "0 0.4rem" } : {}),
    }}>
      {children}
    </div>
  );
}

const STAR_SEP = "* ~ * ~ * ~ * ~ * ~ * ~ * ~ * ~ *";
const DASH_SEP = "--------------------------------";

export default function TicketPreview({
  ticketType = "flash",
  orderUrl = "",
  expiryDisplay = "",
  serial = null,
  tagline = null,
  subtitle = null,
  hasPancake = true,
  showSignature = true,
}) {
  const IS_CUSTOM = ticketType === "custom";
  const IS_MAGIC = ticketType === "magic";
  const IS_PROMO = ticketType === "promo";
  const sep = IS_MAGIC || IS_PROMO ? STAR_SEP : DASH_SEP;

  const [qrSrc, setQrSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(orderUrl || "https://thejoyofpancakes.com", {
      width: 300, margin: 2, errorCorrectionLevel: "Q",
      color: { dark: "#000000", light: PAPER },
    }).then(url => { if (alive) setQrSrc(url); }).catch(() => {});
    return () => { alive = false; };
  }, [orderUrl]);

  return (
    <div style={{
      background: PAPER,
      width: "280px",
      padding: "1.1rem 0.8rem 1.4rem",
      textAlign: "center",
      borderRadius: "4px",
      boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
      // Torn-edge suggestion top + bottom
      maskImage: "linear-gradient(black, black)",
    }}>
      {IS_PROMO ? (
        <>
          <Line size={2} bold reverse>{"  GOLDEN TICKET  "}</Line>
          <Line>The Joy of Pancakes</Line>
          {!tagline && <Line>~ admit one ~</Line>}
          <Line>{sep}</Line>
          {serial && <Line bold>{serial}</Line>}
          {expiryDisplay && <><div style={{ height: "0.4rem" }} /><Line h2 bold reverse>{`  ${expiryDisplay}  `}</Line></>}
          <Line>{sep}</Line>
          {tagline && <Line>{tagline}</Line>}
          <Line>{subtitle || "Scan to begin >>"}</Line>
        </>
      ) : (
        <>
          <Line size={2} bold reverse={IS_CUSTOM || IS_MAGIC}>
            {IS_CUSTOM || IS_MAGIC ? "  GOLDEN TICKET  " : "GOLDEN TICKET"}
          </Line>
          <Line>The Joy of Pancakes</Line>
          <Line>~ admit one ~</Line>
          <Line>{sep}</Line>
          {IS_CUSTOM && <Line h2 bold reverse>{"  * CUSTOM *  "}</Line>}
          {IS_MAGIC && <Line h2 bold reverse>{" * -*- MAGIC -*- * "}</Line>}
          {!IS_CUSTOM && !IS_MAGIC && <Line h2 bold>FLASH</Line>}
          <Line h2 bold>{expiryDisplay}</Line>
          <Line>{sep}</Line>
          <Line>{"Scan to begin >>"}</Line>
        </>
      )}

      {qrSrc ? (
        <img src={qrSrc} alt="ticket QR" width={150} height={150} style={{ margin: "0.7rem auto 0.4rem", display: "block", imageRendering: "pixelated" }} />
      ) : (
        <div style={{ width: 150, height: 150, margin: "0.7rem auto 0.4rem" }} />
      )}

      <Line>{sep}</Line>
      <div style={{ height: "0.6rem" }} />
      {showSignature && (
        <>
          <Line>x_______________________</Line>
          <div style={{ height: "0.6rem" }} />
        </>
      )}
      {IS_MAGIC ? (
        <>
          <Line>A gift from us.</Line>
          <Line>Yours forever.</Line>
        </>
      ) : IS_PROMO && !hasPancake ? (
        <>
          <Line>Made with joy in St. Louis.</Line>
          <Line>See you at the griddle.</Line>
        </>
      ) : (
        <>
          <Line>Your pancake lives here.</Line>
          <Line>Scan anytime, forever.</Line>
        </>
      )}
    </div>
  );
}
